import { Injectable, Signal, computed, effect, inject, signal } from '@angular/core';
import { UserSettingsService } from './user-settings.service';
import { NexusTheme } from '../models/api.models';

// The 4 values that are real <html> classes. A `system-*` value is NEVER a class.
const CONCRETE_THEMES: NexusTheme[] = ['dark-flat', 'dark-glass', 'light-flat', 'light-glass'];
const THEME_STORAGE = 'nexus.theme';
const DARK_SCHEME_QUERY = '(prefers-color-scheme: dark)';

@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly userSettings = inject(UserSettingsService);

  // Tracks the OS colour-scheme preference; updated live via matchMedia below.
  private readonly osPrefersDark = signal<boolean>(
    typeof window !== 'undefined' &&
      window.matchMedia?.(DARK_SCHEME_QUERY).matches === true,
  );

  /** The user's chosen theme, verbatim (may be a `system-*` value). Drives the picker. */
  readonly chosenTheme: Signal<NexusTheme> = computed(() => this.userSettings.theme());

  /** The concrete theme actually rendered - `system-*` resolved against the OS scheme. */
  readonly effectiveTheme: Signal<NexusTheme> = computed(() =>
    this.resolve(this.chosenTheme(), this.osPrefersDark()),
  );

  constructor() {
    // Live-follow the OS scheme so an active `system-*` theme re-resolves on change.
    if (typeof window !== 'undefined' && window.matchMedia) {
      window
        .matchMedia(DARK_SCHEME_QUERY)
        .addEventListener('change', (e) => this.osPrefersDark.set(e.matches));
    }

    effect(() => {
      this.applyTheme(this.effectiveTheme(), this.chosenTheme());
    });
  }

  /** Resolves a possibly-`system-*` theme to a concrete class name. */
  private resolve(theme: NexusTheme, osDark: boolean): NexusTheme {
    if (theme === 'system-flat' || theme === 'system-glass') {
      const surface = theme === 'system-glass' ? 'glass' : 'flat';
      return `${osDark ? 'dark' : 'light'}-${surface}` as NexusTheme;
    }
    return theme;
  }

  private applyTheme(effective: NexusTheme, chosen: NexusTheme): void {
    const root = document.documentElement;
    root.classList.remove(...CONCRETE_THEMES);
    root.classList.add(effective);
    // Persist the CHOSEN theme (may be `system-*`) so the pre-boot script can
    // re-resolve it against the OS on the next load. Never store the resolved value.
    localStorage.setItem(THEME_STORAGE, chosen);
  }
}
